import React, { useEffect, useRef, useState } from "react";
import SharedButton from "../../components/Shared/SharedButton";

import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx-js-style";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import CustomTable from "../../components/Shared/CustomTable";
import moment from "moment";
import { getCostCenter } from "../../store/slices/CostCenterSlice";

import AutoCompleteField from "../../components/Shared/AutoCompleteField";
import DatePicker from "../../components/Shared/DatePicker";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import useFetchData from "../../hooks/useFetchData";
import { EyeIcon } from "../../components/Shared/TableActionIcons";
import { useNavigate } from "react-router-dom";

export default function AccountStatementMain() {
  const { t } = useTranslation();
  const [endDate, setEndDate] = useState(
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0],
  );
  const [startDate, setStartDate] = useState(getOneMonthAgo(endDate));
  const [accountId, setAccountId] = useState(null);
  const [costCenter, setCostCenter] = useState(null);

  const {
    data: accountStatementList,
    loading: isLoading,
    executeFetch,
  } = useFetchData();
  const { costCenters } = useSelector((state) => state.costCenter);
  const dispatch = useDispatch();
  const balanceSheetRef = useRef(null);
  const iframeRef = useRef(null);
  const lang = i18next.language;
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const debitTotal =
    accountStatementList?.data?.entries?.reduce((sum, entry) => {
      return (
        sum +
        (entry?.EntryType?.toLowerCase() === "credit"
          ? 0
          : Number(entry?.Amount || 0))
      );
    }, 0) || 0;

  const creditTotal =
    accountStatementList?.data?.entries?.reduce((sum, entry) => {
      return (
        sum +
        (entry?.EntryType?.toLowerCase() === "debit"
          ? 0
          : Number(entry?.Amount || 0))
      );
    }, 0) || 0;

  const totalBalance = Math.abs(debitTotal - creditTotal);

  const handleSubmit = () => {
    if (startDate && endDate && accountId) {
      executeFetch(
        "POST",
        `reports/getAccountStatement?account_code=${accountId?.value}&start_date=${startDate}&end_date=${endDate}&cost_center_id=${Number(costCenter?.value) || ""}`,
      );
    }
  };

  const handleReset = () => {
    const today = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const defaultStartDate = getOneMonthAgo(today);
    setEndDate(today);
    setStartDate(defaultStartDate);
    setCostCenter(null);
    executeFetch(
      "POST",
      `reports/getAccountStatement?account_code=${accountId?.value}&start_date=${defaultStartDate}&end_date=${today}&cost_center_id=${0}`,
    );
  };

  const { user } = useSelector((state) => state.auth);

  const handlePrint = () => {
    const direction = lang == "ar" ? "rtl" : "ltr";
    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;

    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const businessAddress = user?.Business?.address || "";
    const userEmail = user?.email || "";
    const userMobile = user?.mobile_number || "";
    const businessLogo = user?.Business?.logo || user?.Business?.logo_url;

    // Build Summary HTML
    const openingBalance = accountStatementList?.data?.opening_balance || 0;
    const closingBalance = accountStatementList?.data?.closing_balance || 0;

    // Build Table HTML
    const entries = accountStatementList?.data?.entries || [];

    const rowsHTML = entries
      .map((entry, index) => {
        const isDebit =
          entry.EntryType && entry.EntryType.toLowerCase() === "debit";
        const debitAmount = isDebit ? Number(entry.Amount) : 0;
        const creditAmount = !isDebit ? Number(entry.Amount) : 0;

        return `
        <tr style="background-color: #ffffff; border-bottom: 1px solid #d1d5db;">
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: center;">${index + 1}</td>
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: center;">${moment(entry.Date).format("DD-MM-YYYY HH:mm")}</td>
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: center;">${entry.JournalNumber}</td>
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: right;">${debitAmount !== 0 ? debitAmount.toFixed(2) : "0.00"}</td>
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: right;">${creditAmount !== 0 ? creditAmount.toFixed(2) : "0.00"}</td>
          <td style="padding: 8px; border-right: 1px solid #d1d5db; text-align: left;">${entry.Description || "-"}</td>
          <td style="padding: 8px; text-align: right; font-weight: 600;">${Number(entry.Balance).toFixed(2)}</td>
        </tr>
      `;
      })
      .join("");

    const tableHTML = `
      <div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; margin-top: 5px;">
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background-color: #d1d5db; color: #111827; font-weight: 600;">
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 5%;">#</th>
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 15%;">${t("entry_date")}</th>
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 10%;">${t("entry_number")}</th>
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 10%;">${t("total_debit")}</th>
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 10%;">${t("total_credit")}</th>
            <th style="padding: 8px; border-right: 1px solid #9ca3af; width: 35%;">${t("description")}</th>
            <th style="padding: 8px; width: 15%;">${t("balance")}</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHTML}
        </tbody>
      </table>
      </div>

      <div style="display: flex; gap: 20px; justify-content: flex-end; margin-top: 15px;">
        <div>
          <span style="font-weight: bold; font-size: 16px; color: #111827;">${t("Total Debit")}: </span>
          <span style="font-size: 16px; color: #111827;">${debitTotal.toFixed(2)}</span>
        </div>
        <div>
          <span style="font-weight: bold; font-size: 16px; color: #111827;">${t("Total Credit")}: </span>
          <span style="font-size: 16px; color: #111827;">${creditTotal.toFixed(2)}</span>
        </div>
      </div>
    `;

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Account Statement</title>
          <style>
             @media print {
              @page { size: A4 landscape; margin: 15mm; }
              body { margin: 0; padding: 0; direction: ${direction}; font-family: 'Inter', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; color: #1f2937; }
              header, footer { display: none; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; }
              .summary-box { background-color: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px; padding: 10px; flex: 1; }
             }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 10px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            
            <!-- Column 1: Contact Info -->
            <div style="flex: 1; text-align: start; font-size: 13px; color: #333; line-height: 1.6;">
              ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
              ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
              ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
            </div>

            <!-- Column 2: Logo and Main Info (Center) -->
            <div style="flex: 1; text-align: center;">
              ${businessLogo ? `<img src="${businessLogo}" style="height: 75px; margin-bottom: 8px; object-fit: contain;" />` : ""}
              ${businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameEn}</div>` : ""}
              ${businessNameAr && businessNameAr !== businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameAr}</div>` : ""}
              <div style="font-weight: bold; font-size: 16px; color: #1f2937; margin-top: 8px;">${t("Account Statement")}</div>
              <div style="font-size: 12px; color: #555; margin-top: 5px;">${t("From")}: ${moment(startDate).format("DD-MM-YYYY")} - ${t("To")}: ${moment(endDate).format("DD-MM-YYYY")}</div>
            </div>

            <!-- Column 3: Contact Info (Mirrored) -->
            <div style="flex: 1; text-align: end; font-size: 13px; color: #333; line-height: 1.6;">
              ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
              ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
              ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
            </div>
            
          </div>
          
          <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 10px;">
             <div>
                <div style="font-size: 14px; color: #374151;">
                   <span style="font-weight: bold;">${t("Account")}:</span> ${lang == "en" ? accountStatementList?.data?.account?.name_code_en : accountStatementList?.data?.account?.name_code_ar}
                </div>
             </div>
             
             <div style="display: flex; gap: 20px; justify-content: flex-end;">
                <div>
                  <span style="font-weight: bold; font-size: 16px; color: #111827;">${t("opening_balance")}: </span>
                  <span style="font-size: 16px; color: #111827;">${openingBalance}</span>
                </div>
                <div>
                  <span style="font-weight: bold; font-size: 16px; color: #111827;">${t("closing_balance")}: </span>
                  <span style="font-size: 16px; color: #111827;">${closingBalance}</span>
                </div>
             </div>
          </div>

          ${tableHTML}

        </body>
      </html>
    `);
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const extractData = (element) => {
    const data = [];
    const title = element.querySelector(".text-lg").textContent;
    data.push([title]);
    data.push(["Item", "Amount"]);

    const items = element.querySelectorAll(".flex.justify-between");
    items.forEach((item) => {
      const name = item
        .querySelector(".font-medium")
        .textContent.replace(":", "");
      const amount = item.querySelector("span:last-child").textContent;
      data.push([name, amount]);
    });

    return data;
  };

  const handleExcelExport = () => {
    if (!accountStatementList?.data?.account) {
      console.error("No data available for export");
      return;
    }

    const businessName =
      lang === "ar"
        ? user?.Business?.name_ar || user?.Business?.name_en
        : user?.Business?.name_en || user?.Business?.name_ar;
    const businessAddress = user?.Business?.address || "";
    const userEmail = user?.email || "";
    const userMobile = user?.mobile_number || "";

    let contactParts = [];
    if (businessAddress) contactParts.push(businessAddress);
    if (userEmail) contactParts.push(userEmail);
    if (userMobile) contactParts.push(userMobile);
    const contactInfo = contactParts.join(" • ");

    const accountName =
      lang === "en"
        ? accountStatementList.data.account.name_code_en
        : accountStatementList.data.account.name_code_ar;

    const openingBalance = Number(
      accountStatementList.data.opening_balance || 0,
    );
    const closingBalance = Number(
      accountStatementList.data.closing_balance || 0,
    );

    // Data Rows
    const dataRows = accountStatementList.data.entries.map((entry, index) => {
      const isDebit =
        entry.EntryType && entry.EntryType.toLowerCase() === "debit";
      const debitAmount = isDebit ? Number(entry.Amount) : 0;
      const creditAmount = !isDebit ? Number(entry.Amount) : 0;

      return [
        index + 1,
        moment(entry.Date).format("DD-MM-YYYY HH:mm"),
        entry.JournalNumber,
        debitAmount,
        creditAmount,
        entry.Description || "",
        Number(entry.Balance),
      ];
    });

    // Header Row
    const headerRow = [
      "#",
      t("entry_date"),
      t("entry_number"),
      t("total_debit"),
      t("total_credit"),
      t("description"),
      t("balance"),
    ];

    // Construct Sheet Data
    const ws_data = [
      [businessName], // A1
      [`${t("Account Statement")}`], // A2
      [
        `${t("From")}: ${moment(startDate).format("DD-MM-YYYY")}   ${t("To")}: ${moment(endDate).format("DD-MM-YYYY")}`,
      ], // A3
      [], // A4
      [`${t("Account")}: ${accountName}`], // A5
      [
        `${t("opening_balance")}: ${openingBalance}   ${t("closing_balance")}: ${closingBalance}`,
      ], // A6
      [], // A7
      headerRow, // A8
      ...dataRows, // A9...
      ["", "", t("total"), debitTotal, creditTotal, "", ""],
      [],
      ["", "", "", "", `${t("Total Debit")}:`, debitTotal.toFixed(2), ""],
      ["", "", "", "", `${t("Total Credit")}:`, creditTotal.toFixed(2), ""],
      ["", "", "", "", `${t("Total Balance")}:`, totalBalance.toFixed(2), ""],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(ws_data);

    // Merges
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, // Business Name
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, // Title
      { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }, // Date
      { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } }, // Account Name
      { s: { r: 5, c: 0 }, e: { r: 5, c: 6 } }, // Balances
    ];

    const borderStyle = {
      top: { style: "thin", color: { rgb: "9CA3AF" } },
      bottom: { style: "thin", color: { rgb: "9CA3AF" } },
      left: { style: "thin", color: { rgb: "9CA3AF" } },
      right: { style: "thin", color: { rgb: "9CA3AF" } },
    };

    for (let r = 0; r < 3; r++) {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          font: { bold: true, sz: r === 0 ? 16 : 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    [4, 5].forEach((r) => {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          font: { bold: true, sz: 12, color: { rgb: "000000" } },
          fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: borderStyle,
        };
      }
    });

    for (let c = 0; c < 7; c++) {
      const cellRef = XLSX.utils.encode_cell({ c, r: 7 }); // headerRow is at index 7
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          border: borderStyle,
        };
      }
    }

    // Styles for data rows
    for (let r = 8; r < ws_data.length; r++) {
      for (let c = 0; c < 7; c++) {
        const cellRef = XLSX.utils.encode_cell({ c, r });
        if (!worksheet[cellRef]) {
          worksheet[cellRef] = { v: "", t: "s" };
        }
        worksheet[cellRef].s = {
          ...(worksheet[cellRef].s || {}),
          border: borderStyle,
        };

        // Ensure numbers are aligned to the right, and text to the left for better readability
        if (!worksheet[cellRef].s.alignment) {
          worksheet[cellRef].s.alignment = {
            horizontal: c >= 3 ? "right" : "left",
            vertical: "center",
          };
        }
      }
    }

    // Column Widths
    worksheet["!cols"] = [
      { wch: 5 }, // #
      { wch: 20 }, // Date
      { wch: 15 }, // Journal
      { wch: 15 }, // Debit
      { wch: 15 }, // Credit
      { wch: 40 }, // Desc
      { wch: 15 }, // Balance
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      lang === "en" ? "Account Statement" : "كشف الحساب",
    );

    XLSX.writeFile(
      workbook,
      lang === "en" ? "Account_Statement.xlsx" : "كشف_الحساب.xlsx",
    );
  };

  useEffect(() => {
    dispatch(getCostCenter());
  }, [dispatch]);
  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1;
  };

  const tempelateBody = (rowData) => {
    return (
      <>
        <EyeIcon
          onClick={() => {
            navigate(
              `/manager/report/account-statement-entries/${rowData?.JournalNumber}`,
            );
          }}
          title={t("view")}
        />
      </>
    );
  };

  const columns = [
    { body: tempelateBody, header: t("Actions") },
    {
      body: sequenceBodyTemplate,
      header: t("no"),
      style: { width: "3rem" },
    },
    {
      field: "Date",
      header: t("entry_date"),
      body: (rowData) => moment(rowData.Date).format("DD-MM-YYYY"),
    },
    {
      field: "JournalNumber",
      header: t("entry_number"),
    },

    {
      field: "total_debit",
      body: (data) =>
        data?.EntryType.toLowerCase() == "credit" ? 0 : data?.Amount,
      header: t("Total Debit"),
    },
    {
      field: "total_credit",
      body: (data) =>
        data?.EntryType.toLowerCase() == "debit" ? 0 : data?.Amount,
      header: t("Total Credit"),
    },
    {
      field: "Description",
      header: t("description"),
    },
    {
      field: "Balance",
      header: t("balance"),
    },
  ];
  const formatAccountOptions = (data) => {
    return data
      ? Object.entries(data).map(([id, account]) => ({
          value: Number(account?.code),
          label:
            lang === "ar"
              ? `${account.name_ar} ${account?.code}`
              : `${account.name_en} ${account?.code}`,
        }))
      : [];
  };

  return (
    <div>
      <div className="flex justify-end">
        <div className="w-full md:w-fit flex gap-2 flex-col sm:flex-row">
          <AutoCompleteField
            useFormik={false}
            placeholder={`${t("select")} ${t("account")}`}
            value={accountId}
            onChange={(e) => setAccountId(e.value)}
            fieldUrl="searchAllAccounts"
            optionsFormat={formatAccountOptions}
          />

          <DatePicker
            useFormik={false}
            value={startDate ? new Date(startDate) : null}
            onChange={(e) => {
              setStartDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />
          <DatePicker
            useFormik={false}
            value={endDate ? new Date(endDate) : null}
            onChange={(e) => {
              setEndDate(
                e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              );
            }}
          />
          <DropdownComponent
            useFormik={false}
            value={costCenter}
            onChange={(e) => setCostCenter(e.value)}
            options={
              costCenters?.data
                ? Object.entries(costCenters.data).map(([id, center]) => ({
                    label: lang === "ar" ? center.name_ar : center.name_en,
                    value: Number(center.id),
                  }))
                : []
            }
            placeholder={t("Select Cost Center")}
          />
          <div className="flex items-center justify-end gap-2">
            <SharedButton
              title={t("apply_filter")}
              icon="pi pi-filter"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!startDate || !endDate || !accountId || isLoading}
            />
            <SharedButton
              title={t("Reset")}
              icon="pi pi-sync"
              buttonType="reset"
              onClick={handleReset}
              disabled={isLoading || !accountId}
            />
            <SharedButton
              title={t("Export Excel")}
              icon={"pi pi-file-excel"}
              iconSize={20}
              onClick={() => handleExcelExport()}
            />
            <SharedButton
              title={t("Export PDF")}
              icon={"pi pi-print"}
              iconSize={20}
              onClick={() => handlePrint()}
            />
          </div>
        </div>
      </div>
      {accountStatementList?.data && (
        <div className="this_div" ref={balanceSheetRef}>
          <div className="pb-2">
            <div className="title !mt-2 mb-2">
              <div className="flex justify-center">
                <div className="mx-10 !mt-1">
                  <div className="font-semibold text-black/70 text-xl pb-2">{`${t(
                    `${
                      lang == "en"
                        ? accountStatementList?.data?.account?.name_code_en
                        : accountStatementList?.data?.account?.name_code_ar
                    }`,
                  )}`}</div>
                </div>
              </div>
            </div>

            <div className="">
              <div
                className=""
                pt={{
                  content: { className: "!py-0" },
                }}
              >
                <div className="">
                  <div>
                    <div className="flex gap-4 justify-end my-3">
                      <div>
                        <span className="font-bold text-[18px]">
                          {t("opening_balance")}:{" "}
                        </span>
                        <span className="text-[18px]">
                          {" "}
                          {accountStatementList?.data?.opening_balance}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-[18px]">
                          {t("closing_balance")}:{" "}
                        </span>
                        <span className="text-[18px]">
                          {accountStatementList?.data?.closing_balance}
                        </span>
                      </div>
                    </div>
                  </div>
                  <CustomTable
                    data={accountStatementList?.data?.entries}
                    columns={columns}
                  />
                  <div className="flex gap-4 justify-end my-3">
                    <div>
                      <span className="font-bold text-[18px]">
                        {t("Total Debit")}:{" "}
                      </span>
                      <span className="text-[18px]">
                        {debitTotal.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-[18px]">
                        {t("Total Credit")}:{" "}
                      </span>
                      <span className="text-[18px]">
                        {creditTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </div>
  );
}
